import {useAppDispatch, useArtwork, useArtworkOffCanvas} from "../../../../redux/stateHooks";
import {useScreenSizeDetector} from "../../../../hooks/useScreenSizeDetector";
import {useMinimumQuantity} from "../../../../hooks/useMinimumQuantity";
import {
    Alert,
    Button,
    Card,
    Col,
    Container,
    FloatingLabel,
    Form,
    InputGroup,
    Offcanvas,
    Row,
    Spinner
} from "react-bootstrap";
import {AiOutlineClose} from "react-icons/ai";
import * as React from "react";
import {DesignServices} from "./designServices";
import {QuantityInputGroup} from "./quantityInputGroup";
import {MinimumQuantityNotice} from "./minimumQuantityNotice";
import {artworkOffCanvasActions} from "../../../../redux/slices/artworkOffCanvas";
import {useCreateItem} from "../../../../hooks/addToCart/useCreateItem";
import {useAddToCartDisabled} from "../../../../hooks/addToCart/useAddToCartDisabled";
import {useCustomerArtworkQuery} from "../../../../hooks/useCustomerArtworkQuery";
import {useContentUpload} from "../../../../hooks/useFileUpload";
import {useNotesChange} from "../../../../hooks/useNotesChange";
import {acceptedFileExtensions} from "../../../../utils/helpers";
import {useCreateTextOnlyArtwork} from "../../../../hooks/useCreateTextOnlyArtwork";
import {useProductDetailsQuery} from "../../../../hooks/useProductDetailsQuery";
import {useAppContext} from "../../../../hooks/useAppContext";
import {useAddToCartQuery} from "../../../../hooks/useAddToCartQuery";
import {useQuantity} from "../../../../hooks/useQuantity";
import {RemoveArtworkButton} from "./removeArtworkButton";

interface IOffCanvasContentProps {
    show: boolean,
    onHide: () => void,
    children: React.ReactNode
}

export function ArtworkOffCanvas() {
    const {model: {isTextOnly}} = useCustomerArtworkQuery();
    const dispatch = useAppDispatch()
    const {show} = useArtworkOffCanvas();

    const onHide = () => {
        dispatch(artworkOffCanvasActions.setShow(false))
    }

    const children = isTextOnly ? <NotesOnlyContent/> : <UploadFormContent/>;

    return (
        <OffCanvasContent show={show} onHide={onHide}>
            {children}
        </OffCanvasContent>
    );
}

const OffCanvasContent: React.FC<IOffCanvasContentProps> = ({children, show, onHide}: IOffCanvasContentProps) => {
    const {model: {isTextOnly, isArtworkProduct}} = useCustomerArtworkQuery();
    const {isSmallScreenSize} = useScreenSizeDetector();
    const {model: {isStandardProduct}} = useAddToCartQuery();

    return (
        <>
            <Offcanvas id="artwork-offcanvas" placement="end" show={show} onHide={onHide}>
                <Offcanvas.Header
                    className="d-flex justify-content-between align-items-start border-bottom border-2 pb-1 px-3">
                    <Offcanvas.Title className="fs-3 fw-normal">
                        {((isArtworkProduct && !isTextOnly) || !isStandardProduct) ? (
                            <>Upload Artwork</>
                        ) : (
                            <>Add Your Text</>
                        )}
                        {((isArtworkProduct || !isStandardProduct) && !isTextOnly) &&
                            <span className="fw-normal fs-6 d-block">
                                    {isSmallScreenSize ? (
                                        <>Upload your artwork now, or later.</>
                                    ) : (
                                        <>Upload now, or we'll send you a link to upload later.</>
                                    )}
                                </span>
                        }
                    </Offcanvas.Title>
                    <Button variant="link" aria-label="Close and Continue Shopping" className="fs-5"
                            onClick={onHide}>
                        <AiOutlineClose className="ms-1" size={20} aria-hidden/>
                    </Button>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0">
                    {children}
                    <AddToCartContent/>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
}

const Notes: React.FC<{
    maxLength?: number;
    label: string;
}> = ({label, maxLength}) => {
    const {model: {notes}, onChange} = useNotesChange();

    return (
        <FloatingLabel label={label}>
            <Form.Control
                as="textarea"
                placeholder={label}
                style={{height: "100px"}}
                value={notes}
                maxLength={maxLength}
                onChange={onChange}/>
        </FloatingLabel>
    );
};

const UploadForm = () => {
    const {
        isLoading,
        isError,
        onChange,
        reset,
        input: {
            ref,
            file,
            preview
        }
    } = useContentUpload();

    const artwork = useArtwork();
    const {model: {artworkGuidelineTypePath}} = useProductDetailsQuery();

    return (
        <Form>
            <Alert className="mt-3 mb-3 border bg-light">
                <p className="mb-2">
                    <small> If your file is too large or you need to attach more than 1,
                        you can upload a zip folder or{' '}
                        <a href="/ContactUs" className="fw-bold text-decoration-underline" tabIndex={0} role="link"
                           aria-label="Contact us information.">
                            contact us{' '}
                        </a>
                        for additional submission options.
                    </small>
                </p>
                <p className="mb-0">
                    {artworkGuidelineTypePath && (
                        <a href={artworkGuidelineTypePath} target="_blank" className="d-block" tabIndex={0} role="link"
                           aria-label="View templates & guidelines">
                            <small>
                                View templates & guidelines
                            </small>
                        </a>
                    )}
                </p>
            </Alert>
            <Form.Group className="my-2">
                <Form.Label className="visually-hidden" htmlFor="artwork-upload-submit">
                    Upload Artwork
                </Form.Label>

                <Form.Control
                    ref={ref}
                    type="file"
                    id="artwork-upload-submit"
                    className="form-control"
                    accept={acceptedFileExtensions.join(', ')}
                    disabled={isLoading}
                    aria-invalid={isError}
                    aria-describedby="uploadError"
                    onChange={onChange}
                />
            </Form.Group>
            <small className="text-muted">
                Accepted File Formats:{' '}
                <span className="fst-italic">
                    {acceptedFileExtensions.join(', ')}
                </span>
            </small>

            {file && artwork?.id && !isError &&
                <div className="bg-light p-3">
                    <FilePreview file={file} src={preview}/>
                    <RemoveArtworkButton file={file} reset={reset}/>
                </div>
            }
            {isLoading &&
                <div className="text-center my-3 fs-5">
                    <Spinner animation="border" variant="secondary" role="status" size="sm"
                             className="text-center me-1"/>
                    <span>Uploading your file...</span>
                </div>
            }
            {isError &&
                <p className="text-danger">File upload failed, please try again.</p>
            }
            {!isError && artwork?.id &&
                <div className="mt-3">
                    <Notes label="Artwork Instructions (Optional)"/>
                </div>
            }
        </Form>
    )
}

const FilePreview = (props: { file: File | undefined, src: string | undefined }) => (
    <>
        {(props.file && props.src) &&
            <div className="img-thumbnail border-0 d-block" style={{backgroundColor: "transparent"}}>
                <img src={props.src} alt={props.file.name} className="artwork-file-preview"/>
            </div>
        }
    </>
);

const UploadFormContent = () => (
    <div id="offcanvas-upload" className="px-3 mb-5">
        <UploadForm/>
    </div>
);

const NotesOnlyContent = () => (
    <Card body className="px-3">
        <Notes label="Enter Custom Text Here (Optional)" maxLength={200}/>
    </Card>
);

const AddToCartContent = () => {
    const {minimumSaleQuantity} = useMinimumQuantity();
    const {quantity} = useQuantity();
    const {createItem} = useCreateItem()
    const {createTextOnlyArtwork} = useCreateTextOnlyArtwork();
    const addToCartDisabled = useAddToCartDisabled();
    const {model: {sku}} = useProductDetailsQuery();
    const {productId} = useAppContext();

    return <Container
        className="atc-offcanvas-footer sticky-bottom bg-white px-4 pt-1 pb-3 border-top border-2">
        <Row>
            <Col className="px-0">
                <div className="mt-3">

                    <div className="mb-3"><DesignServices location='offcanvas'/></div>
                    <InputGroup>
                        <QuantityInputGroup/>
                        <Button
                            variant="warning"
                            size="lg"
                            disabled={addToCartDisabled}
                            onClick={async () => {
                                const artworkId = await createTextOnlyArtwork()
                                await createItem(artworkId);
                            }}
                            className="flex-fill add-to-cart-btn ms-1"
                            data-ee-list={`ProductNew | Details`}
                            data-react="true"
                            data-item-id={productId}
                            data-quantity={quantity}
                            data-sku={sku}
                        >
                            Add to Cart
                        </Button>
                    </InputGroup>
                    {minimumSaleQuantity > 1 && <MinimumQuantityNotice minimumQuantity={minimumSaleQuantity}/>}
                </div>
            </Col>
        </Row>
    </Container>;
};